import {
  CATEGORY_LIMITS,
  FUNDING_SOURCE_LIMITS,
  LEVEL_LIMITS,
  NO_LIMIT,
  DISABLED,
} from '#src/constants/limits';
import { type MachineContextType } from '#src/state-machine/types';
import { DefaultFilterValues, type Limits, type ViewType } from '#src/types';

export function buildLevelLimit(context: Omit<MachineContextType, 'data'>) {
  if (context.levelCompare.length > 0) {
    return {};
  }

  const levelLimits = LEVEL_LIMITS[context.level] ?? LEVEL_LIMITS.default;
  const viewTypeLimits = levelLimits?.[context.viewType];

  if (viewTypeLimits?.comparisonLimit == 0) {
    return {};
  }

  return { levelLimit: viewTypeLimits?.comparisonLimit };
}

export function buildCategoryLimit(context: Omit<MachineContextType, 'data'>) {
  if (
    context.categoryCompare.length > 0 ||
    context.category === DefaultFilterValues.category
  ) {
    return {};
  }

  const categoryLimits = CATEGORY_LIMITS[context.viewType];
  const comparisonLimit = categoryLimits?.comparisonLimit;

  // Don't send limit if NO_LIMIT (0) or DISABLED (1)
  if (
    comparisonLimit == null ||
    comparisonLimit === NO_LIMIT ||
    comparisonLimit === DISABLED
  ) {
    return {};
  }

  // Special case: EMK category uses 4 instead of 5 for chart view
  if (context.viewType === 'chart' && context.category === 'EMK') {
    return { categoryLimit: 4 };
  }

  return { categoryLimit: comparisonLimit };
}

export function buildFundingSourceLimit(
  context: Omit<MachineContextType, 'data'>
) {
  if (
    context.fundingSourceCompare.length > 0 ||
    context.fundingSource === DefaultFilterValues.fundingSource
  ) {
    return {};
  }

  const fundingSourceLimits = FUNDING_SOURCE_LIMITS[context.viewType];
  const comparisonLimit = fundingSourceLimits?.comparisonLimit;

  // Don't send limit if NO_LIMIT (0) or DISABLED (1)
  if (
    comparisonLimit == null ||
    comparisonLimit === NO_LIMIT ||
    comparisonLimit === DISABLED
  ) {
    return {};
  }

  return { fundingSourceLimit: comparisonLimit };
}

export function buildTopEntriesParams(
  context: Omit<MachineContextType, 'data'>
) {
  if (
    context.level === DefaultFilterValues.level &&
    context.category === DefaultFilterValues.category
  ) {
    // Even at GLB+ALL, we should still send funding source limit
    return {
      ...buildFundingSourceLimit(context),
    };
  }

  if (context.level === DefaultFilterValues.level) {
    return {
      ...buildCategoryLimit(context),
      ...buildFundingSourceLimit(context),
    };
  }

  return {
    ...buildLevelLimit(context),
    ...buildCategoryLimit(context),
    ...buildFundingSourceLimit(context),
  };
}

export function getLevelLimitsByViewType(viewType: ViewType) {
  const entries: [string, Limits | undefined][] = Object.entries(
    LEVEL_LIMITS
  ).map(([level, limits]) => {
    return [level, limits[viewType]];
  });

  return entries.reduce(
    (acc, [level, limit]) => {
      if (limit != null) {
        acc[level] = limit;
      }
      return acc;
    },
    {} as Record<string, Limits>
  );
}
